// Intentionally clamp negative Delta T values into the lowest schema bucket.
const normalizeDeltaTValue = (rawValue: string | number): number | null => {
  const numericValue = Number(rawValue);
  if (!Number.isFinite(numericValue)) {
    return null;
  }
  return numericValue < 0 ? 0 : numericValue;
};

// Bucket labels are normalized before matching by lowercasing and removing
// whitespace, so values like "10 - 20 °C" become "10-20°c". This regex only
// cares about the numeric range portion at the start of that normalized label:
// capture group 1 is the lower bound and capture group 2 is the upper bound.
export const DELTA_T_RANGE_LABEL_PATTERN = /(\d+)-(\d+)/;

// After the same normalization step, labels like ">30 °C" become ">30°c".
// This regex extracts the numeric threshold after the ">" sign into capture
// group 1 so the code can compare the computed Delta T against it.
export const DELTA_T_GREATER_THAN_LABEL_PATTERN = />(\d+)/;

const normalizeOptions = (options: unknown): Array<{ id: string; label: string }> =>
  (Array.isArray(options) ? options : [])
    .map((option) => {
      if (typeof option === 'string') {
        return { id: option, label: option };
      }
      if (
        option &&
        typeof option === 'object' &&
        typeof (option as { id?: unknown }).id === 'string' &&
        typeof (option as { label?: unknown }).label === 'string'
      ) {
        return option as { id: string; label: string };
      }
      return null;
    })
    .filter((option): option is { id: string; label: string } => !!option);

export const resolveDeltaTBucketValue = (
  options: unknown,
  rawValue: string | number
): string | number | null => {
  const normalizedNumericValue = normalizeDeltaTValue(rawValue);
  if (normalizedNumericValue === null) {
    return null;
  }

  const matchedOption = normalizeOptions(options).find(({ label }) => {
    const normalized = label.toLowerCase().replace(/\s+/g, '');
    const rangeMatch = normalized.match(DELTA_T_RANGE_LABEL_PATTERN);
    if (rangeMatch) {
      const min = Number(rangeMatch[1]);
      const max = Number(rangeMatch[2]);
      return (
        (min === 0 ? normalizedNumericValue >= min : normalizedNumericValue > min) &&
        normalizedNumericValue <= max
      );
    }

    const greaterThanMatch = normalized.match(DELTA_T_GREATER_THAN_LABEL_PATTERN);
    if (greaterThanMatch) {
      return normalizedNumericValue > Number(greaterThanMatch[1]);
    }

    return false;
  });

  return matchedOption?.id ?? null;
};
